import { Typography, Paper, IconButton, Box, Tooltip } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { CustomPaper } from '../../../../../../components/CustomPaper';
import { icons } from '../../../../../../utils/icons';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import SimpleModal from '../../../../../../components/modal/simpleModal';
import { saveAs } from 'file-saver'
import { KeyCaptionVertical } from '../../../../../../components/detailKeyValue/keyCaption.index';
import { formatFileSize, getReadableFileType } from '../methos';
import { isFileSizeValid } from '../../../main/components/allFiles/constans/validationsFile';
import { Types } from '../../../../../../utils/enums';
import { SnackSimple } from '../../../../../../components';
import { allowedFormats } from '../../../../utils/constans/formats';

interface FileUploadProps {
  label?: string;
  required?: boolean;
  file: File | null;
  setFile: Dispatch<SetStateAction<File | null>>;
}

export const FileUpload = ({ label = 'Archivo *', required = false, file, setFile }: FileUploadProps) => {


  const styles = useStyles()
  const { t } = useTranslation()


  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dataFile, setDataFile] = useState({ type: '', size: '' })

  const [modal, setModal] = useState<{
    open: boolean
    title: string
    description?: string
    children?: JSX.Element
    removeCancel?: boolean
    confirm: () => void
  }>({
    open: false,
    title: '',
    description: '',
    confirm: () => { },
  })

  const [openSnackbar, setOpenSnackbar] = useState({
    open: false,
    message: '',
    type: Types.SUCCESS,
  })

  const closeModal = () => {
    setModal(prev => ({
      ...prev,
      open: false,
    }))
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: Object.keys(allowedFormats).reduce((acc, ext) => {
      acc[`.${ext}`] = [];
      return acc;
    }, {} as Record<string, string[]>),
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const newFile = acceptedFiles[0];

        // Verificación extra por extensión (por si el navegador no la valida bien)
        const fileExt = newFile.name.split('.').pop()?.toLowerCase();
        if (!fileExt || !allowedFormats[fileExt as keyof typeof allowedFormats]) {
          setOpenSnackbar({
            open: true,
            message: t('filesManager.snackbar.unsupportedFileType'),
            type: Types.ERROR,
          });
          return;
        }

        const { valid, category, max } = isFileSizeValid(newFile);

        if (!valid) {
          setOpenSnackbar((prev) => ({
            ...prev,
            message: t(`filesManager.snackbar.theFileIsTooLarge_${category}`, { max }),
            type: Types.ERROR,
            open: true,
          }))
          return;
        }

        //para ajustar el nombre
        const sanitizedFileName = newFile.name.replace(/\s+/g, '_');
        const renamedFile = new File([newFile], sanitizedFileName, {
          type: newFile.type,
          lastModified: newFile.lastModified,
        });

        if (file) {
          setModal(prev => ({
            ...prev,
            open: true,
            title: t('filesManager.modal.titles.youWantToReplaceThisFile'),
            confirm: () => {
              setFile(renamedFile)
              closeModal();
            }
          }));
        } else {
          // No hay archivo, subir directamente
          setFile(renamedFile);
        }
      }
    }
  });

  const handleLoad = async () => {
    if (file) {
      const blob = await file.arrayBuffer();
      const blobObj = new Blob([blob], { type: file.type });
      saveAs(blobObj, file.name);
    }
  }

  useEffect(() => {
    if (file) {
      setDataFile({
        type: getReadableFileType(file),
        size: formatFileSize(file.size),
      });
    }
  }, [file]);



  const closeOpenSnakbar = () => {
    setOpenSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }


  return (
    <CustomPaper elevation={3} sx={styles.customPaper}>
      <Typography variant='text_grey' sx={styles.label}>{label}</Typography>
      {!file ?
        <Paper
          variant="outlined"
          sx={styles.paper(isDragActive)}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          {icons('uploadFile')}
          <Typography variant='text_grey'>
            Seleccione un archivo<br />o arrastre aquí
          </Typography>
        </Paper>
        :
        <Box sx={styles.containerFileData} {...getRootProps()}>
          <input {...getInputProps()} ref={inputRef} />
          <Box sx={styles.items}>
            <Tooltip title={file.name} placement="top" arrow>
              <Typography
                className="bold full-w title-three"
                sx={styles.itemTitle}
              >
                {file.name ?? ''}
              </Typography>
            </Tooltip>
            <Box sx={styles.iconContainer}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
                className="download"
              >
                {icons('swapHoriz')}
              </IconButton>

              <IconButton
                className="download"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLoad();
                  //onDeleteFile()
                }}
              >
                {icons('download')}
              </IconButton>
            </Box>

          </Box>

          {file &&
            <Box sx={styles.containerType}>
              <Box sx={styles.containerSecond}>
                <KeyCaptionVertical captionProps={{ sx: { width: '48%', '&.MuiTypography-root': {  wordBreak: 'normal', overflowWrap: 'normal' }/* , border:'1px solid red' */ } }} valueProps={{ sx: { width: '50%'/* , border:'1px solid red' */ } }} value={t('filesManager.titles.typeFile')} caption={dataFile.type} />
                <KeyCaptionVertical captionProps={{ sx: { width: '48%'/* , border:'1px solid red' */ } }} valueProps={{ sx: { width: '50%'/* , border:'1px solid red' */ } }} value={t('filesManager.titles.sizeFile')} caption={dataFile.size} />
              </Box>
            </Box>
          }
        </Box>
      }

      <SnackSimple
        close={closeOpenSnakbar}
        open={openSnackbar.open}
        type={openSnackbar.type}
        message={openSnackbar.message}
      />

      <SimpleModal
        open={!!modal.open}
        next={modal.confirm}
        back={closeModal}
        close={closeModal}
        description={modal.description}
        buttonText={t('general.confirm')}
        cancelText={t('general.cancel')}
        title={modal.title}
        loadingButton={false}
      />

    </CustomPaper>
  );
};
